export type Weekday = "SUN" | "MON" | "TUE" | "WED" | "THU" | "FRI" | "SAT";

export type SimpleCadence = "minutes" | "hours" | "days" | "daily" | "weekly";

export interface SimpleScheduleState {
  cadence: SimpleCadence;
  interval: number;
  minute: number;
  hour: number;
  daysOfWeek: Weekday[];
}

export interface CronTimerValue {
  cron?: string;
  timezone?: string;
}

export type CronBuilderMode = "simple" | "advanced";

export const WEEKDAY_SEQUENCE: Weekday[] = [
  "SUN",
  "MON",
  "TUE",
  "WED",
  "THU",
  "FRI",
  "SAT",
];

const WEEKDAY_LABEL_MAP: Record<Weekday, string> = {
  SUN: "Sunday",
  MON: "Monday",
  TUE: "Tuesday",
  WED: "Wednesday",
  THU: "Thursday",
  FRI: "Friday",
  SAT: "Saturday",
};

export const WEEKDAY_OPTIONS = WEEKDAY_SEQUENCE.map((value) => ({
  value,
  label: WEEKDAY_LABEL_MAP[value],
}));

export const DEFAULT_SIMPLE_SCHEDULE: SimpleScheduleState = {
  cadence: "days",
  interval: 1,
  minute: 0,
  hour: 17,
  daysOfWeek: ["MON"],
};

const clamp = (value: number, min: number, max?: number): number => {
  if (Number.isNaN(value)) {
    return min;
  }
  const lowered = Math.max(value, min);
  if (typeof max === "number") {
    return Math.min(lowered, max);
  }
  return lowered;
};

const pad = (value: number): string => value.toString().padStart(2, "0");

const buildWeeklyDays = (days: Weekday[]): string =>
  days.length > 0 ? days.join(",") : "*";

export const buildCronFromSimpleState = (
  state: SimpleScheduleState,
): string => {
  const minute = clamp(Math.trunc(state.minute), 0, 59);
  const hour = clamp(Math.trunc(state.hour), 0, 23);
  const interval = clamp(Math.trunc(state.interval), 1);

  switch (state.cadence) {
    case "minutes":
      return `*/${interval} * * * *`;
    case "hours":
      return `${minute} */${interval} * * *`;
    case "days":
      return `${minute} ${hour} */${interval} * *`;
    case "daily":
    case "weekly":
      return `${minute} ${hour} * * ${buildWeeklyDays(state.daysOfWeek)}`;
    default:
      return `*/${interval} * * * *`;
  }
};

const MINUTE_INTERVAL_REGEX = /^\*\/(\d+) \* \* \* \*$/;
const HOURLY_REGEX = /^(\d{1,2}) \*\/(\d+) \* \* \*$/;
const DAILY_REGEX = /^(\d{1,2}) (\d{1,2}) \*\/(\d+) \* \*$/;
const WEEKLY_REGEX = /^(\d{1,2}) (\d{1,2}) \* \* ([A-Z,]+)$/i;

const parseWeekdays = (list: string): Weekday[] => {
  if (list.trim() === "*") {
    return [...WEEKDAY_SEQUENCE];
  }
  return list
    .toUpperCase()
    .split(",")
    .filter((value): value is Weekday =>
      WEEKDAY_SEQUENCE.includes(value as Weekday),
    );
};

export const parseCronToSimpleState = (
  expr: string,
): SimpleScheduleState | null => {
  if (!expr) {
    return null;
  }

  const minuteMatch = expr.match(MINUTE_INTERVAL_REGEX);
  if (minuteMatch) {
    return {
      cadence: "minutes",
      interval: clamp(parseInt(minuteMatch[1], 10), 1),
      minute: 0,
      hour: 0,
      daysOfWeek: ["MON"],
    };
  }

  const hourlyMatch = expr.match(HOURLY_REGEX);
  if (hourlyMatch) {
    return {
      cadence: "hours",
      minute: clamp(parseInt(hourlyMatch[1], 10), 0, 59),
      interval: clamp(parseInt(hourlyMatch[2], 10), 1),
      hour: 0,
      daysOfWeek: ["MON"],
    };
  }

  const dailyMatch = expr.match(DAILY_REGEX);
  if (dailyMatch) {
    return {
      cadence: "days",
      minute: clamp(parseInt(dailyMatch[1], 10), 0, 59),
      hour: clamp(parseInt(dailyMatch[2], 10), 0, 23),
      interval: clamp(parseInt(dailyMatch[3], 10), 1),
      daysOfWeek: ["MON"],
    };
  }

  const weeklyMatch = expr.match(WEEKLY_REGEX);
  if (weeklyMatch) {
    const parsedDays = parseWeekdays(weeklyMatch[3]);
    return {
      cadence: parsedDays.length > 1 ? "daily" : "weekly",
      minute: clamp(parseInt(weeklyMatch[1], 10), 0, 59),
      hour: clamp(parseInt(weeklyMatch[2], 10), 0, 23),
      interval: 1,
      daysOfWeek: parsedDays,
    };
  }

  return null;
};

const formatTimeOfDay = (hour: number, minute: number): string => {
  const normalizedHour = clamp(Math.trunc(hour), 0, 23);
  const normalizedMinute = clamp(Math.trunc(minute), 0, 59);
  const suffix = normalizedHour >= 12 ? "PM" : "AM";
  const hour12 = normalizedHour % 12 === 0 ? 12 : normalizedHour % 12;
  return `${hour12}:${pad(normalizedMinute)} ${suffix}`;
};

export const summarizeSimpleState = (
  state: SimpleScheduleState,
  timezone?: string,
): string => {
  const tzSuffix = timezone ? ` (${timezone})` : "";
  const intervalLabel = state.interval === 1 ? "" : ` ${state.interval}`;

  switch (state.cadence) {
    case "minutes":
      return `Every${intervalLabel} ${state.interval === 1 ? "minute" : "minutes"}`;
    case "hours":
      return `Every${intervalLabel} hour${state.interval === 1 ? "" : "s"} at minute ${clamp(state.minute, 0, 59)}`;
    case "days":
      return `Every${intervalLabel} day${state.interval === 1 ? "" : "s"} at ${formatTimeOfDay(state.hour, state.minute)}${tzSuffix}`;
    case "daily":
    case "weekly": {
      const dayList = state.daysOfWeek.length
        ? state.daysOfWeek.map((day) => WEEKDAY_LABEL_MAP[day]).join(", ")
        : "day";
      if (state.cadence === "daily") {
        return `Daily on ${dayList} at ${formatTimeOfDay(state.hour, state.minute)}${tzSuffix}`;
      }
      return `Every ${dayList} at ${formatTimeOfDay(state.hour, state.minute)}${tzSuffix}`;
    }
    default:
      return "Every minute";
  }
};

export const isValidCronExpression = (expr: string): boolean => {
  const parts = expr.trim().split(/\s+/);
  return parts.length === 5 && parts.every((part) => part.length > 0);
};

export const deriveModeFromValue = (
  value?: CronTimerValue,
): CronBuilderMode => {
  if (value?.cron) {
    return parseCronToSimpleState(value.cron) ? "simple" : "advanced";
  }

  return "simple";
};

export const getTimezoneList = (): string[] => {
  const fallback = [
    "UTC",
    "America/Los_Angeles",
    "America/New_York",
    "Europe/London",
    "Europe/Berlin",
    "Asia/Tokyo",
    "Asia/Singapore",
    "Australia/Sydney",
  ];

  try {
    const intl = Intl as typeof Intl & {
      supportedValuesOf?: (key: string) => string[];
    };
    if (intl.supportedValuesOf) {
      return intl.supportedValuesOf("timeZone");
    }
  } catch (_error) {
    // ignore and fall back to curated list
  }

  return fallback;
};
